import { useCallback, useEffect, useMemo, useState } from "react"

import { Button, cn } from "@coval/ui"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { buildScopedStorageKey } from "@/utils/scopedStorageKey"

import { PORTAL_FLOW } from "../constants"
import { useConvertCta } from "../hooks/useConvertCta"
import type { PortalFlow, SubscriptionResponse, UsageResponse } from "../types"
import {
  usageBandCopy,
  usageBandState,
  usageCapacityAction,
  usageLadderApplies,
} from "../utils/usageBand"

const TRIAL_WARNING_DAYS = 3

// Dismissal is per notice, not per organization: dismissing the 80% rung must
// not also dismiss the trial reminder, nor keep the 90% rung hidden.
const dismissStorageKey = (orgId: string, specKey: string) =>
  buildScopedStorageKey(
    `coval:billing-banner-dismissed:${specKey}`,
    "organization",
    { organizationId: orgId },
  )

interface BillingStatusBannerProps {
  className?: string
  organizationId: string | null
  subscription: SubscriptionResponse | null
  usage?: UsageResponse | null
  onPortalAction: (flow: PortalFlow) => void
  isPortalLoading?: boolean
}

interface BannerSpec {
  /** Identifies the notice for dismissal, so each rung is dismissed on its own. */
  key: string
  tone: "warning" | "blocking"
  title: string
  body: string
  ctaLabel: string
  ctaFlow: PortalFlow
  /** Set when the action is a conversation rather than a self-serve flow. */
  ctaHref?: string
  dismissible: boolean
}

function daysUntil(iso: string | null): number | null {
  if (iso === null) {
    return null
  }
  const target = new Date(iso).getTime()
  if (Number.isNaN(target)) {
    return null
  }
  const ms = target - Date.now()
  return Math.ceil(ms / (1000 * 60 * 60 * 24))
}

function specFor(subscription: SubscriptionResponse): BannerSpec | null {
  const { status, trial_end } = subscription

  if (status === "trialing") {
    const days = daysUntil(trial_end)
    if (days === null) {
      return null
    }
    if (days <= 0) {
      // trial_end already passed but Stripe hasn't moved status yet
      return {
        tone: "blocking",
        key: "trial-ended",
        title: "Your free trial has ended.",
        body: "Upgrade to continue using Coval.",
        ctaLabel: "Upgrade now",
        ctaFlow: PORTAL_FLOW.manageSubscription,
        dismissible: false,
      }
    }
    if (days > TRIAL_WARNING_DAYS) {
      return null
    }
    return {
      tone: "warning",
      key: "trial-warning",
      title: `Trial ends in ${days} ${days === 1 ? "day" : "days"}.`,
      body: "Your plan starts automatically when your trial ends.",
      ctaLabel: "Manage plan",
      ctaFlow: PORTAL_FLOW.manageSubscription,
      dismissible: true,
    }
  }

  // past_due: still in grace / entitled — a card update lets Stripe's retry clear it.
  if (status === "past_due") {
    return {
      tone: "blocking",
      key: "past-due",
      title: "Payment failed.",
      body: "Update your payment method to keep your subscription active.",
      ctaLabel: "Update payment",
      ctaFlow: PORTAL_FLOW.updatePayment,
      dismissible: false,
    }
  }

  // unpaid: grace exhausted, access suspended — but the sub is still linked, so
  // convert_eligible is false and this routes to the portal, not a new signup.
  if (status === "unpaid") {
    return {
      tone: "blocking",
      key: "unpaid",
      title: "Your subscription is past due.",
      body: "Settle your outstanding balance to restore access to Coval.",
      ctaLabel: "Update payment",
      ctaFlow: PORTAL_FLOW.updatePayment,
      dismissible: false,
    }
  }

  if (status === "canceled") {
    return {
      tone: "blocking",
      key: "canceled",
      title: "Your subscription has ended.",
      body: "Resubscribe to continue using Coval.",
      ctaLabel: "Resubscribe",
      ctaFlow: PORTAL_FLOW.manageSubscription,
      dismissible: false,
    }
  }

  return null
}

// One rung of the usage ladder. Nothing is enforced from here, so no rung says
// what happens at the limit — the gate's own refusal speaks when one is enforced.
function usageSpec(
  subscription: SubscriptionResponse,
  usage: UsageResponse | null | undefined,
): BannerSpec | null {
  if (subscription.status !== "active" && subscription.status !== "trialing") {
    return null
  }
  const state = usageBandState(subscription, usage)
  if (state === null) {
    return null
  }
  if (!usageLadderApplies(subscription) && state.band !== "over") {
    return null
  }
  const { title, body } = usageBandCopy(state, subscription)
  const capacity = usageCapacityAction(subscription)
  return {
    key: `usage-${state.dimension}-${state.band}`,
    tone: state.band === "over" ? "blocking" : "warning",
    title,
    body,
    ctaLabel: capacity.label,
    ctaFlow: PORTAL_FLOW.manageSubscription,
    ctaHref: capacity.href,
    // The lower two rungs are informational, so they can be put away; from 98%
    // the notice stays, because the next thing that happens is the allowance
    // running out.
    dismissible: state.band === "approaching" || state.band === "close",
  }
}

export function BillingStatusBanner({
  className,
  organizationId,
  subscription,
  usage,
  onPortalAction,
  isPortalLoading,
}: BillingStatusBannerProps) {
  const { trackEvent } = useAnalytics()
  const spec = useMemo(() => {
    if (subscription === null) {
      return null
    }
    const statusSpec = specFor(subscription)
    // A subscription state the customer has to act on (payment failed, trial
    // over) outranks usage. A dismissible nudge does not: putting away "trial
    // ends in 3 days" must not also put away where the org stands on its
    // allowance.
    if (statusSpec !== null && !statusSpec.dismissible) {
      return statusSpec
    }
    return usageSpec(subscription, usage) ?? statusSpec
  }, [subscription, usage])
  const [dismissed, setDismissed] = useState(false)
  const goToConvert = useConvertCta()
  const convertEligible = subscription?.convert_eligible ?? false
  const convertLabel =
    subscription?.billing_source === "stripe" && spec !== null
      ? spec.ctaLabel
      : "Subscribe to Coval"

  useEffect(() => {
    if (organizationId === null) {
      return
    }
    if (!spec?.dismissible) {
      setDismissed(false)
      return
    }
    const storageKey = dismissStorageKey(organizationId, spec.key)
    if (storageKey === undefined) {
      setDismissed(false)
      return
    }
    try {
      const stored = sessionStorage.getItem(storageKey)
      setDismissed(stored === "true")
    } catch {
      setDismissed(false)
    }
  }, [organizationId, spec])

  const handleDismiss = useCallback(() => {
    if (organizationId === null || spec === null) {
      return
    }
    const storageKey = dismissStorageKey(organizationId, spec.key)
    try {
      if (storageKey !== undefined) {
        sessionStorage.setItem(storageKey, "true")
      }
    } catch {
      // ignore storage errors
    }
    setDismissed(true)
    // Analytics is secondary and must never break the dismiss flow — fire it
    // last, guarded, so a throw can't keep the banner stuck on screen.
    try {
      trackEvent(TRACKING_EVENTS.BILLING.BANNER_DISMISSED, {
        subscription_status: subscription?.status,
        tone: spec.tone,
      })
    } catch {
      // swallow — billing UX takes priority over an analytics event
    }
  }, [organizationId, spec, trackEvent, subscription?.status])

  if (spec === null || dismissed) {
    return null
  }

  // Text uses the tone's on-surface token so it stays legible on the filled
  // surface in both themes (the dark-mode fills are light, so on-surface is dark).
  const toneClasses =
    spec.tone === "warning"
      ? "dark:border-coval-warning-border bg-coval-warning-surface text-coval-warning-on-surface"
      : "dark:border-coval-danger-border bg-coval-danger-surface text-coval-danger-on-surface"

  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col gap-3 rounded-lg p-4 dark:border sm:flex-row sm:items-center sm:justify-between",
        toneClasses,
        className,
      )}
    >
      <div>
        <p className="text-sm font-medium">{spec.title}</p>
        <p className="text-sm">{spec.body}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {spec.dismissible && (
          <Button
            variant="ghost"
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="shrink-0"
          >
            Dismiss
          </Button>
        )}
        {convertEligible ? (
          <Button
            onClick={() => goToConvert("banner", subscription?.status)}
            aria-label={convertLabel}
            className="shrink-0"
          >
            {convertLabel}
          </Button>
        ) : spec.ctaHref !== undefined ? (
          <Button asChild className="shrink-0">
            <a href={spec.ctaHref} aria-label={spec.ctaLabel}>
              {spec.ctaLabel}
            </a>
          </Button>
        ) : (
          <Button
            onClick={() => onPortalAction(spec.ctaFlow)}
            disabled={isPortalLoading}
            aria-label={spec.ctaLabel}
            className="shrink-0"
          >
            {isPortalLoading ? "Opening…" : spec.ctaLabel}
          </Button>
        )}
      </div>
    </div>
  )
}
